import { selectRole, selectUserGroup } from '../../store/authReducer';
import { getStoreState } from '../../store/storeRef';
import { hasRole, RoleTypes } from '../components/Permission';
import { findPermissionRef, viewPermissionRefs } from '../components/UserGroupPermissions';

// Statuses that mean "you aren't allowed to do this" rather than "something
// broke". The UI already hides the controls these calls back, so toasting them
// is noise for every user below Operator.
const SILENT_STATUS_CODES = [401, 403];

// baseApi returns these synthetic statuses before a request is even attempted
// (no token yet, site settings still loading). Same story: not actionable.
const SILENT_STATUS_STRINGS = ['UNAUTHENTICATED', 'SITE_SETTINGS_NOT_LOADED'];

/**
 * Errors reach the toast layer in three shapes:
 *  - api.js rejections: `{ responseStatus, ...body }`, where the body may also
 *    carry its own `statusCode`
 *  - client-side throws: `{ statusCode }`
 *  - RTK Query rejections via `unwrap()`: `{ status, data }`
 */
export const getErrorStatus = (error: any): number | string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const status = error.statusCode ?? error.responseStatus ?? error.status ?? error.originalStatus;

  return typeof status === 'number' || typeof status === 'string' ? status : null;
};

export const isSilentError = (error: any): boolean => {
  const status = getErrorStatus(error);

  if (typeof status === 'number') {
    return SILENT_STATUS_CODES.includes(status);
  }

  if (typeof status === 'string') {
    return SILENT_STATUS_STRINGS.includes(status);
  }

  return false;
};

/**
 * Mirrors ErrorMessageHandler: raw server payloads go to Operator and above (or
 * anyone holding system.admin); everyone else gets a generic message.
 */
export const canViewErrorDetails = (): boolean => {
  const state = getStoreState();

  // No store wired (unit tests, isolated renders) - the policy can't be
  // evaluated, so fall back to the detailed toast rather than masking the error.
  if (!state) {
    return true;
  }

  const role = selectRole(state);
  const userGroup = selectUserGroup(state);

  return (
    hasRole(role, RoleTypes.Operator) ||
    !!findPermissionRef(userGroup?.permissions ?? [], viewPermissionRefs.admin)
  );
};
